import { useMutation } from '@tanstack/react-query';
import { useDispatch } from 'react-redux';
import { initialLoading, showRegistrationPopup } from '~/actions';
import { useAppSelector } from '~/hooks/useAppSelector';
import { fireCompleted } from '~/posthogs/events/Signup';
import { apiMethodsConst } from '~/services/methods';
import { UpdateOfficeSetupService } from '../services/UpdateOfficeSetupService';
import type { IUpdateOfficeSetupRequest } from '../types/UpdateOfficeSetup';
import { suppressPostSignupPopups } from '../utils/suppressPostSignupPopups';

export const useUpdateOfficeSetup = () => {
  const dispatch = useDispatch();
  const currentUser = useAppSelector((state) => state.user?.user ?? null);
  const existingSpecialtiesAndProcedures = useAppSelector((state) => {
    const edu = state.user?.officeEducation as
      | { specialtiesAndProcedures?: Record<string, string[]> }
      | null;
    return edu?.specialtiesAndProcedures ?? {};
  });

  return useMutation<void, unknown, IUpdateOfficeSetupRequest>({
    mutationFn: (payload) =>
      UpdateOfficeSetupService.updateOfficeSetup({
        input: payload,
        existingSpecialtiesAndProcedures,
      }),
    onSuccess: (_data, payload) => {
      if (!currentUser?.id) return;
      const existingOfficeInfo = currentUser.office_info ?? {};
      dispatch({
        type: apiMethodsConst.EDIT_USER_REQUEST,
        payload: {
          ...currentUser,
          ...(payload.isProfileComplete && { is_profile_complete: true }),
          // Parking lives at the user root to match legacy ParkingInfoPopup's
          // read path (`!user.parkingInformation`).
          parkingInformation: payload.parkingInformation,
          office_info: {
            ...existingOfficeInfo,
            timePerPatientRDH: payload.timePerPatientRDH,
            timePerPatientDA: payload.timePerPatientDA,
            timePerPatientDN: payload.timePerPatientDN,
            isAssistedHygiene: payload.isAssistedHygiene,
            is_info_asked: payload.isInfoAsked,
          },
        },
      });

      if (payload.isProfileComplete) {
        suppressPostSignupPopups(String(currentUser.id));
        fireCompleted();
        dispatch(showRegistrationPopup());
        // Re-run the watchInitialLoading saga so PostHog identifies the new
        // user (TnCOffices FF flips on, OFFICE_TNC consent fetch can fire and
        // surface the popup) and state.userCredentials is rehydrated from BE
        // (so /account/information shows the saved Software/Insurance pills
        // without a hard refresh).
        dispatch(initialLoading());
      }
    },
  });
};
